import { createElement, useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { ArrowLeft, X, type LucideIcon } from 'lucide-react';
import { onBack } from '../lib/backstack';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
const variants: Record<Variant, string> = {
  primary: 'bg-forest-700 text-white hover:bg-forest-800 active:bg-forest-800',
  secondary: 'bg-white text-ink border border-line hover:bg-forest-50',
  outline: 'bg-white text-ink border border-line hover:bg-forest-50',
  ghost: 'text-muted hover:bg-forest-50',
  danger: 'bg-t-red text-danger hover:bg-[#f9d7d4]',
};

export function Button({ variant = 'primary', size = 'md', className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg' }) {
  // Sense design guide: pill buttons, 52 / 44 / 36 px, SemiBold; press scales to 0.98, disabled is 50 %.
  const s = size === 'lg' ? 'h-13 px-6 text-base' : size === 'sm' ? 'h-9 px-4 text-xs' : 'h-11 px-5 text-sm';
  return <button className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold transition active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 ${s} ${variants[variant]} ${className}`} {...rest} />;
}

export const tints = {
  green: 'bg-t-green text-t-green-fg',
  amber: 'bg-t-amber text-t-amber-fg',
  blue: 'bg-t-blue text-t-blue-fg',
  slate: 'bg-t-slate text-t-slate-fg',
  rose: 'bg-t-rose text-t-rose-fg',
  teal: 'bg-t-teal text-t-teal-fg',
  violet: 'bg-t-violet text-t-violet-fg',
  red: 'bg-t-red text-t-red-fg',
  solid: 'bg-primary text-white',
} as const;
export type Tint = keyof typeof tints;

export function IconTile({ icon: Icon, tint, size = 44 }: { icon: LucideIcon; tint: Tint; size?: number }) {
  return (
    <span className={`flex shrink-0 items-center justify-center rounded-xl ${tints[tint]}`} style={{ width: size, height: size }}>
      <Icon size={Math.round(size * 0.5)} aria-hidden />
    </span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="num hidden rounded-md lg:inline border border-line bg-white px-1.5 py-0.5 text-2xs font-semibold text-ink shadow-card">{children}</kbd>;
}

export function Field({ label, hint, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-muted">{label}</span>
      <input id={id} className="h-11 w-full min-w-0 rounded-xl border border-line bg-white px-4 text-base outline-none placeholder:text-hint focus:border-primary focus:ring-4 focus:ring-primary/10" {...rest} />
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  );
}

export function SelectField({ label, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-muted">{label}</span>
      <select id={id} className="h-11 w-full min-w-0 rounded-xl border border-line bg-white px-4 text-base outline-none focus:border-forest-600" {...rest}>{children}</select>
    </label>
  );
}

/**
 * Full page on phones, centred dialog on desktop. Esc, the back arrow and the phone's / browser's Back
 * button close it. The footer stays pinned above the keyboard and the system navigation bar.
 */
export function Sheet({ open, onClose, title, children, wide = false, footer }: { open: boolean; onClose(): void; title: ReactNode; children: ReactNode; wide?: boolean; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close.current(); } };
    window.addEventListener('keydown', onKey, true);
    // Back button: each open page adds a history entry, so Back closes it instead of leaving the app.
    const release = onBack(() => close.current());
    // Focus the first field in the body (never the close button, so Enter doesn't dismiss).
    setTimeout(() => {
      if (ref.current?.contains(document.activeElement)) return; // user already moved into a field
      const body = ref.current?.querySelector('[data-sheet-body]');
      // Touch screens: only fields marked autoFocus (quantity, weight) open the keyboard; elsewhere it
      // waits for a tap, so a page doesn't open half-covered by the keyboard.
      const touch = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const field = touch ? body?.querySelector<HTMLElement>('[autofocus],[data-autofocus]') : body?.querySelector<HTMLElement>('input,select,textarea');
      (field ?? (touch ? null : body?.querySelector<HTMLElement>('button')) ?? ref.current)?.focus({ preventScroll: true });
    }, 30);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      release();
      prev?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="anim-fade fixed inset-0 z-50 lg:flex lg:items-center lg:justify-center lg:bg-ink/40" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}
        className={`anim-page flex h-full w-full flex-col bg-paper pt-[var(--sat)] lg:h-auto lg:max-h-[92dvh] lg:rounded-3xl lg:bg-white lg:pt-0 lg:shadow-lift ${wide ? 'lg:max-w-3xl' : 'lg:max-w-lg'}`}>
        <div className="flex items-center gap-2 border-b border-line bg-white px-2 py-2 lg:gap-3 lg:border-0 lg:bg-transparent lg:px-5 lg:pt-4 lg:pb-2">
          <button aria-label="Back" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-forest-50 lg:hidden"><ArrowLeft size={22} /></button>
          <h2 id={titleId} className="min-w-0 flex-1 truncate text-lg font-semibold">{title}</h2>
          <button aria-label="Close" onClick={onClose} className="hidden h-10 w-10 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 lg:flex"><X size={20} /></button>
        </div>
        <div data-sheet-body className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 pt-4 pb-4 lg:px-5 lg:pt-0">{children}</div>
        {footer && <div className="border-t border-line bg-white px-4 pt-3 pb-[max(0.75rem,var(--sab))] lg:px-5 lg:pb-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Empty({ icon: Icon, title, children, action }: { icon: LucideIcon; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <div aria-hidden className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-surface-2 text-ink-3"><Icon size={28} /></div>
      <div className="font-semibold">{title}</div>
      {children && <div className="mt-1 max-w-xs text-sm text-ink-3">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Grey placeholder block while data loads. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span aria-hidden className={`block animate-pulse rounded-xl bg-forest-200 ${className}`} />;
}

/** A list-shaped skeleton: avatar + two lines per row. */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading" className="flex flex-col gap-4 py-4">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2"><Skeleton className="h-3.5 w-2/5" /><Skeleton className="h-3 w-1/4" /></div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return <section className={`rounded-card border border-line bg-white shadow-card ${className}`}>{children}</section>;
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="text-2xs font-semibold tracking-wider text-ink-3 uppercase">{children}</div>;
}

/** Small KPI card: tinted icon, label and a number. */
export function StatCard({ icon, tint, label, value, sub }: { icon: LucideIcon; tint: Tint; label: string; value: string; sub?: string }) {
  return (
    // Sense Fresh KPI tile: the accent tint with a small uppercase label and a 23 px Bold number.
    <div className={`min-w-0 rounded-card p-3.5 sm:p-4 ${tints[tint].split(' ')[0]}`}>
      <div className={`flex items-center gap-1 truncate text-2xs font-semibold tracking-wider uppercase ${tints[tint].split(' ')[1]}`}>{createElement(icon, { size: 12, 'aria-hidden': true })}{label}</div>
      <div className="num mt-1 truncate text-xl font-bold sm:text-2xl">{value}</div>
      {sub && <div className="truncate text-2xs text-ink-3">{sub}</div>}
    </div>
  );
}

/** Segmented control: soft track (16 px), the chosen option as a white 12 px tile. */
export function Segmented<T extends string>({ value, onChange, options, label, full = false }: { value: T; onChange(v: NoInfer<T>): void; options: Array<[NoInfer<T>, string]>; label: string; full?: boolean }) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto rounded-2xl bg-forest-100 p-1">
      {options.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={value === k} onClick={() => onChange(k)}
          className={`h-9 shrink-0 rounded-xl px-4 text-sm font-semibold ${full ? 'flex-1' : ''} ${value === k ? 'bg-white text-ink shadow-card' : 'text-muted hover:text-ink'}`}>{l}</button>
      ))}
    </div>
  );
}

/** On/off switch (Sense Fresh style): brand track with a white knob. */
export function Switch({ checked, onChange, label }: { checked: boolean; onChange(v: boolean): void; label: string }) {
  return (
    <button role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${checked ? 'bg-forest-700' : 'bg-line'}`}>
      <span className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow-card transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  );
}
